import React, { useCallback, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { EColor } from '@frontend/pole-ui/lib/common/constants'
import { EChipModifier } from '@frontend/pole-ui/lib/components/Chip'
import { EIconSize } from '@frontend/pole-ui/lib/components/Icon'
import { OkFillIcon } from '@frontend/pole-ui/lib/components/Icon/svg/OkFillIcon'
import { Modal, useModal } from '@frontend/ui-kit/agromarket/components/Modal'
import type { FundingRequestData } from '@/api/kubik/monolith'
import Badge from '@/components/composed/financing/Badge/Badge'
import { useApplyFinancingOffer } from '@/components/composed/financing/hooks'
import { GenericErrorModal } from '@/components/composed/modals/GenericErrorModal'
import { GenericSuccessModal } from '@/components/composed/modals/GenericSuccessModal'
import { Image, TextWithIcon } from '@/components/ui'
import { useAuthenticateUser } from '@/components/ui/AuthButton/hooks'
import { pushGtmEventOnSubmitOrder } from '@/logic/metrika/financing/gtmHelpers'
import { EGtmBlock } from '@/logic/metrika/financing/types'
import { getUserGtmInfo } from '@/logic/metrika/helpers'
import { getProfile } from '@/logic/profile/reducer'
import userRegion from '@/logic/userRegion'
import type { RootState } from '@/redux/interfaces'
import { EFinancingOfferType, EFinancingProductId } from '@/types'
import { getApplyingFinancingOfferFormInitialValues } from '@/utils/getApplyingFinancingOfferFormInitialValues'
import { isApplyingOfferRequestBody } from '@/utils/hooks/applyingFinancingOffer/typeguard'
import { useWebpExtension } from '@/utils/hooks/image'
import { useActionOnLoggedIn } from '@/utils/hooks/useActionOnLoggedIn'
import { useDeviceType } from '@/utils/hooks/useDeviceType'
import { FIELD_EVALUATION_REPORT_DIALOG_PREVIEW_NAMES } from './constants'
import { messages } from './messages'
import type { TFieldEvaluationReportDialogProps } from './types'
import './styles.scss'

const mapState = (state: RootState) => ({
  profile: getProfile()(state),
  userGtmInfo: getUserGtmInfo()(state),
})

export const FieldEvaluationReportDialog = Modal.create<TFieldEvaluationReportDialogProps>(
  ({ comment }: TFieldEvaluationReportDialogProps) => {
    const { remove: closeModal } = useModal()
    const { profile, userGtmInfo } = useSelector(mapState)
    const { isAuthenticated, openAuthModal } = useAuthenticateUser()
    const imageExtension = useWebpExtension()
    const { isMobile } = useDeviceType()

    const previews = useMemo(() => FIELD_EVALUATION_REPORT_DIALOG_PREVIEW_NAMES.map(name => `${name}.${imageExtension}`), [imageExtension])
    const [shouldRequestAfterAuth, setShouldRequestAfterAuth] = useState(false)
    const { applyFinancingOffer, isLoading } = useApplyFinancingOffer()

    const getRequestBody = useCallback(() => {
      const initialValues = getApplyingFinancingOfferFormInitialValues({
        offerType: EFinancingOfferType.REPORT_CTRL2GO,
        productId: EFinancingProductId.REPORT_CTRL2GO,
        profile,
      })

      return {
        ...initialValues,
        comment,
      }
    }, [comment, profile])

    const createFieldEvaluationReport = useCallback(async () => {
      try {
        const body = getRequestBody()

        if (!isApplyingOfferRequestBody(body)) {
          throw new Error('Invalid request body')
        }

        const response = await applyFinancingOffer({
          body,
          withSuccessModal: false,
        })

        pushGtmEventOnSubmitOrder(
          userGtmInfo,
          userRegion.name,
          response as Pick<FundingRequestData, 'id' | 'multiId'>[],
          [EFinancingProductId.REPORT_CTRL2GO],
          EGtmBlock.REPORT_CTRL2GO,
        )

        Modal.show(GenericSuccessModal, {
          title: messages.successDialog.title,
          subtitle: messages.successDialog.subtitle,
          button: messages.successDialog.submit,
        })
      } catch (error) {
        Modal.show(GenericErrorModal, {
          title: messages.errorDialog.title,
          subtitle: messages.errorDialog.subtitle,
          button: messages.errorDialog.submit,
        })
      } finally {
        closeModal()
      }
    }, [getRequestBody, applyFinancingOffer, userGtmInfo, closeModal])

    const handleRequestReport = useCallback(() => {
      if (isAuthenticated) {
        createFieldEvaluationReport()

        return
      }

      setShouldRequestAfterAuth(true)

      openAuthModal({
        onCloseModal: () => {
          setShouldRequestAfterAuth(false)
        },
      })
    }, [isAuthenticated, openAuthModal, createFieldEvaluationReport])

    useActionOnLoggedIn(() => {
      if (shouldRequestAfterAuth) {
        setShouldRequestAfterAuth(false)
        createFieldEvaluationReport()
      }
    }, shouldRequestAfterAuth)

    return (
      <Modal
        size="sm"
        title={messages.title}
        primaryButton={{
          text: messages.actions.request,
          buttonProps: {
            variant: 'primary',
            isDisabled: isLoading,
          },
          onClick: handleRequestReport,
        }}
      >
        <div className="fieldEvaluationReportDialog">
          <div className="fieldEvaluationReportDialog__badges">
            <Badge className="fieldEvaluationReportDialog__badge" text={messages.badges.available} color={EChipModifier.LIGHT_GREEN} />
            <Badge
              className="fieldEvaluationReportDialog__badge"
              text={isMobile ? messages.badges.forAgriLand.short : messages.badges.forAgriLand.full}
              color={EChipModifier.LIGHT_ORANGE}
            />
          </div>

          <div className="fieldEvaluationReportDialog__images">
            {previews.map((src: string) => (
              <div className="fieldEvaluationReportDialog__image" key={src}>
                <Image src={src} isInStatic />
              </div>
            ))}
          </div>
          <div className="fieldEvaluationReportDialog__caption text_super-small text_small-sm">{messages.imagesCaption}</div>

          <h5 className="fieldEvaluationReportDialog__sectionTitle">{messages.benefits.title}</h5>
          <div className="fieldEvaluationReportDialog__list">
            {messages.benefits.items.map((text: string) => (
              <TextWithIcon
                key={text}
                className="fieldEvaluationReportDialog__listItem text_small"
                iconProps={{ component: <OkFillIcon />, size: EIconSize.M, style: { color: EColor.GREEN } }}
                text={text}
              />
            ))}
          </div>
        </div>
      </Modal>
    )
  },
)
